import { useState, useCallback } from 'react';
import { Box, TextField, Button, CircularProgress, Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import BaseModal from '@/components/modals/BaseModal';
import LanguageSelector from '@/components/selectors/LanguageSelector';
import { StringKeys } from '@/resources/strings/StringKeys';
import { EMAIL_REGEX } from '@/utils/validation/emailValidation';

interface AddEmailModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (email: string, name: string, languageCode: string) => Promise<void>;
}

const AddEmailModal = ({ open, onClose, onSave }: AddEmailModalProps) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [languageCode, setLanguageCode] = useState('en-US');
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<{
    email?: string;
    name?: string;
  }>({});

  const validateForm = useCallback(() => {
    const newErrors: { email?: string; name?: string } = {};

    if (!name.trim()) {
      newErrors.name = t(StringKeys.REQUIRED);
    }

    if (!email.trim()) {
      newErrors.email = t(StringKeys.REQUIRED);
    } else {
      if (!EMAIL_REGEX.test(email)) {
        newErrors.email = t(StringKeys.PLEASE_PROVIDE_A_VALID_EMAIL_ADDRESS);
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [email, name, t]);

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    try {
      await onSave(email, name, languageCode);
      // Reset form on success
      setEmail('');
      setName('');
      setLanguageCode('en-US');
      setErrors({});
      onClose();
    } catch (error) {
      console.error('Error adding email:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setEmail('');
      setName('');
      setLanguageCode('en-US');
      setErrors({});
      onClose();
    }
  };

  return (
    <BaseModal
      open={open}
      onClose={handleClose}
      title={t(StringKeys.ADD_EMERGENCY_RECIPIENT_EMAILS)}
      submitButtonText=""
      onSubmit={() => {}}
      footerContent={undefined}
      showFooter={false}
      size="sm"
    >
      <Box sx={{ pt: 1, pb: 2 }}>
        <Grid container spacing={2}>
          {/* Name Field */}
          <Grid size={{ xs: 12 }}>
            <TextField
              label={t(StringKeys.NAME)}
              placeholder={t(StringKeys.FULL_NAME)}
              fullWidth
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (errors.name) {
                  setErrors({ ...errors, name: undefined });
                }
              }}
              error={!!errors.name}
              helperText={errors.name}
              disabled={isLoading}
              size="small"
            />
          </Grid>

          {/* Email Field */}
          <Grid size={{ xs: 12 }}>
            <TextField
              label={t(StringKeys.EMAIL)}
              placeholder="abc@example.com"
              fullWidth
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (errors.email) {
                  setErrors({ ...errors, email: undefined });
                }
              }}
              error={!!errors.email}
              helperText={errors.email}
              disabled={isLoading}
              size="small"
              type="email"
            />
          </Grid>

          {/* Language Selector */}
          <Grid size={{ xs: 12 }}>
            <LanguageSelector
              value={languageCode}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                setLanguageCode(e.target.value)
              }
              size="small"
              fullWidth
            />
          </Grid>

          {/* Action Buttons */}
          <Grid size={{ xs: 12 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 2,
                mt: 2,
              }}
            >
              <Button
                variant="outlined"
                onClick={handleClose}
                disabled={isLoading}
              >
                {t(StringKeys.CANCEL)}
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={isLoading}
                sx={{
                  minWidth: 100,
                }}
              >
                {isLoading ? (
                  <CircularProgress size={24} sx={{ color: 'white' }} />
                ) : (
                  t(StringKeys.ADD_EMAIL)
                )}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </BaseModal>
  );
};

export default AddEmailModal;
